/**
 * View-switcher preference (#1192).
 *
 * Elevated users (admins + back-office permission presets) have two surfaces:
 * their own "home" dashboard (via `landingPathForUser`) and the agent CRM. The
 * top-header ViewSwitcher lets them flip between the two and REMEMBERS the
 * choice so it survives reload / next session AND governs the index `/`
 * (`RoleRedirect`) default landing.
 *
 * This is wayfinding only — it never affects permissions or route guards. A
 * plain agent has no second view, so the preference is irrelevant to them
 * (the switcher is not shown and RoleRedirect falls through to /crm/dashboard
 * regardless).
 *
 * Persisted as a plain string in localStorage so it can be read synchronously
 * by `RoleRedirect` (a render-time `<Navigate>`), not just by React hooks.
 */
export type ViewPreference = "home" | "crm";

export const VIEW_PREF_STORAGE_KEY = "lwp_view_pref";

/** Read the persisted preference, or null when unset / unavailable. */
export function getViewPreference(): ViewPreference | null {
  try {
    const stored = localStorage.getItem(VIEW_PREF_STORAGE_KEY);
    return stored === "home" || stored === "crm" ? stored : null;
  } catch {
    // localStorage can throw in private-mode / sandboxed contexts.
    return null;
  }
}

/** Persist the preference. Best-effort: swallow storage errors. */
export function setViewPreference(pref: ViewPreference): void {
  try {
    localStorage.setItem(VIEW_PREF_STORAGE_KEY, pref);
  } catch {
    // ignore — preference is a nicety, not load-bearing.
  }
}

/**
 * The landing path for a remembered preference, given the user's home path.
 *
 * `home` → the user's own dashboard (caller passes `landingPathForUser(...)`);
 * `crm`  → the agent CRM dashboard. No preference (or a plain agent whose home
 * IS /crm/dashboard) → the home path unchanged.
 */
export function landingForPreference(
  homePath: string,
  pref: ViewPreference | null,
): string {
  if (homePath === "/crm/dashboard") {
    // Plain agent: only one view, preference is moot.
    return homePath;
  }
  if (pref === "crm") {
    return "/crm/dashboard";
  }
  // pref === "home" or unset → the user's own dashboard.
  return homePath;
}
